import api from '../services/api';
import toast from './toast';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

/**
 * Report utility functions for ad-hoc report generation
 */

// Transform filter parameters for API calls
export const buildFilterParams = (filters) => {
  const params = {};

  filters.forEach((filter, index) => {
    if (!filter.field || !filter.value) return;

    const filterKey = `filter_${filter.field}_${index}`;
    const operator = filter.operator || 'equals';

    switch (operator) {
      case 'equals':
        params[filterKey] = filter.value;
        break;
      case 'not_equals':
        params[`${filterKey}__ne`] = filter.value;
        break;
      case 'contains':
        params[`${filterKey}__icontains`] = filter.value;
        break;
      case 'starts_with':
        params[`${filterKey}__istartswith`] = filter.value;
        break;
      case 'ends_with':
        params[`${filterKey}__iendswith`] = filter.value;
        break;
      case 'greater_than':
        params[`${filterKey}__gt`] = filter.value;
        break;
      case 'less_than':
        params[`${filterKey}__lt`] = filter.value;
        break;
      case 'between':
        if (filter.value2) {
          params[`${filterKey}__gte`] = filter.value;
          params[`${filterKey}__lte`] = filter.value2;
        }
        break;
      case 'before':
        params[`${filterKey}__lt`] = filter.value;
        break;
      case 'after':
        params[`${filterKey}__gt`] = filter.value;
        break;
      default:
        params[filterKey] = filter.value;
    }
  });

  return params;
};

// Generate report data from API
export const generateReport = async (dataSource, selectedFields, filters, sortBy, sortOrder) => {
  try {
    const endpoint = getEndpointForDataSource(dataSource);
    const params = {
      ...buildFilterParams(filters),
      page_size: 1000 // Get all records for reports
    };

    // Add field selection if supported by the endpoint
    if (selectedFields && selectedFields.length > 0) {
      params.fields = selectedFields.join(',');
    }

    // Add sorting
    if (sortBy) {
      params.ordering = sortOrder === 'desc' ? `-${sortBy}` : sortBy;
    }

    console.log('Generating report with params:', params);

    const response = await api.get(endpoint, { params });

    // More robust data extraction with fallbacks
    let data = [];
    let totalCount = 0;

    if (response && response.data) {
      // Handle different response structures
      if (Array.isArray(response.data)) {
        data = response.data;
        totalCount = response.data.length;
      } else if (response.data.results && Array.isArray(response.data.results)) {
        data = response.data.results;
        totalCount = response.data.count || response.data.results.length;
      } else if (typeof response.data === 'object') {
        // If it's a single object, wrap it in an array
        data = [response.data];
        totalCount = 1;
      }
    }

    // Transform data based on selected fields
    const transformedData = selectedFields && selectedFields.length > 0
      ? data.map(item => {
          const transformedItem = {};
          selectedFields.forEach(field => {
            transformedItem[field] = item[field] || '';
          });
          return transformedItem;
        })
      : data;

    return {
      data: transformedData,
      totalCount: totalCount,
      source: dataSource
    };

  } catch (error) {
    console.error('Report generation error:', error);
    // Provide more specific error messages
    if (error.response) {
      throw new Error(`Failed to generate report: ${error.response.status} ${error.response.statusText}`);
    } else if (error.request) {
      throw new Error('Failed to generate report: Network error - please check your connection');
    } else {
      throw new Error(`Failed to generate report: ${error.message}`);
    }
  }
};

// Get endpoint for data source
const getEndpointForDataSource = (dataSource) => {
  const endpoints = {
    employees: '/staff/',
    attendance: '/attendance/',
    leave: '/leave-applications/',
    payroll: '/payroll/',
    training: '/certificates/',
    projects: '/projects/'
  };

  return endpoints[dataSource] || '/staff/';
};

// Preview report (limited results)
export const previewReport = async (dataSource, selectedFields, filters) => {
  try {
    const endpoint = getEndpointForDataSource(dataSource);
    const params = {
      ...buildFilterParams(filters),
      page_size: 10 // Preview only first 10 records
    };

    if (selectedFields && selectedFields.length > 0) {
      params.fields = selectedFields.join(',');
    }

    const response = await api.get(endpoint, { params });

    // More robust data extraction with fallbacks
    let data = [];
    let totalCount = 0;

    if (response && response.data) {
      // Handle different response structures
      if (Array.isArray(response.data)) {
        data = response.data;
        totalCount = response.data.length;
      } else if (response.data.results && Array.isArray(response.data.results)) {
        data = response.data.results;
        totalCount = response.data.count || response.data.results.length;
      } else if (typeof response.data === 'object') {
        // If it's a single object, wrap it in an array
        data = [response.data];
        totalCount = 1;
      }
    }

    return {
      data: data,
      totalCount: totalCount,
      isPreview: true
    };

  } catch (error) {
    console.error('Report preview error:', error);
    // Provide more specific error messages
    if (error.response) {
      throw new Error(`Failed to preview report: ${error.response.status} ${error.response.statusText}`);
    } else if (error.request) {
      throw new Error('Failed to preview report: Network error - please check your connection');
    } else {
      throw new Error(`Failed to preview report: ${error.message}`);
    }
  }
};

// Export report to CSV
export const exportToCSV = (data, filename, selectedFields) => {
  if (!data || data.length === 0) {
    toast.error('No data to export');
    return;
  }

  try {
    // Get headers from selected fields or all keys
    const headers = selectedFields && selectedFields.length > 0
      ? selectedFields
      : Object.keys(data[0]);

    // Create CSV content
    const csvContent = [
      headers.map(header => {
        // Use display name for header if available
        const fieldMatch = selectedFields ? null : null; // We don't have availableFields in CSV export
        return header.replace(/^[^_]+_/, ''); // Remove source prefix for display
      }).join(','),
      ...data.map(row => {
        return headers.map(header => {
          // Remove source prefix to get actual data field name
          const cleanFieldName = header.replace(/^[^_]+_/, '');
          const value = row[cleanFieldName];
          // Escape commas and quotes in CSV
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value || '';
        }).join(',');
      })
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Report exported to ${filename}.csv successfully!`);

  } catch (error) {
    console.error('CSV export error:', error);
    toast.error('Failed to export CSV file');
  }
};

// Export report to Excel
export const exportToExcel = async (data, filename, selectedFields, availableFields, metadata = {}) => {

  if (!data || data.length === 0) {
    toast.error('No data to export');
    return;
  }

  try {
    // Prepare data for Excel
    let excelData = data.map(row => {
      if (selectedFields && selectedFields.length > 0) {
        const filteredRow = {};
        selectedFields.forEach(field => {
          // Remove source prefix from field name to match actual data structure
          // Pattern: source_fieldname -> fieldname
          const cleanFieldName = field.replace(/^[^_]+_/, '');
          const fieldMatch = availableFields?.find(f => f.id === field);
          const displayFieldName = fieldMatch?.label || cleanFieldName;

          // Get the value from the data using the clean field name
          const value = row[cleanFieldName] || '';
          filteredRow[displayFieldName] = value;
        });
        return filteredRow;
      }
      return row;
    });


    // Sort by staff_id if available (check for both original and display field names)
    if (excelData.length > 0 && (excelData[0].staff_id || excelData[0]['Staff ID'])) {
      excelData.sort((a, b) => {
        const staffIdA = a.staff_id || a['Staff ID'] || '';
        const staffIdB = b.staff_id || b['Staff ID'] || '';
        return staffIdA.localeCompare(staffIdB, undefined, { numeric: true, sensitivity: 'base' });
      });
    }

    // Generate charts data
    const charts = generateChartData(data, metadata.dataSource, selectedFields, availableFields);

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Create Report Info sheet
    const infoData = createReportInfoSheet(metadata, data.length);
    const infoWs = XLSX.utils.aoa_to_sheet(infoData);
    XLSX.utils.book_append_sheet(wb, infoWs, 'Report Info');

    // Style the info sheet
    const infoCols = [
      { wch: 25 }, // Label column
      { wch: 50 }  // Value column
    ];
    infoWs['!cols'] = infoCols;

    // Create data sheet
    const dataWs = XLSX.utils.json_to_sheet(excelData);
    XLSX.utils.book_append_sheet(wb, dataWs, 'Report Data');

    // Auto-width columns for data sheet
    const colWidths = selectedFields && selectedFields.length > 0
      ? selectedFields.map(field => {
          const fieldMatch = availableFields?.find(f => f.id === field);
          const displayFieldName = fieldMatch?.label || field.replace(/^[^_]+_/, '');
          return { wch: Math.max(displayFieldName.length, 15) };
        })
      : Object.keys(data[0]).map(key => ({ wch: Math.max(key.length, 15) }));

    dataWs['!cols'] = colWidths;

    // Create Charts Data sheet if charts are available
    if (charts.length > 0) {
      const chartsData = createChartsDataSheet(charts);
      const chartsWs = XLSX.utils.aoa_to_sheet(chartsData);
      XLSX.utils.book_append_sheet(wb, chartsWs, 'Charts Data');

      // Style the charts sheet
      const chartsCols = [
        { wch: 30 }, // Chart title/Label column
        { wch: 15 }, // Value column
        { wch: 12 }  // Percentage column
      ];
      chartsWs['!cols'] = chartsCols;
    }

    // Create Summary Statistics sheet
    if (charts.length > 0) {
      const summaryData = createSummaryStatsSheet(data, selectedFields, charts, metadata);
      const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary Stats');

      // Style the summary sheet
      const summaryCols = [
        { wch: 25 }, // Field/Statistic
        { wch: 15 }, // Value
        { wch: 20 }  // Notes
      ];
      summaryWs['!cols'] = summaryCols;
    }

    // Save file
    XLSX.writeFile(wb, `${filename}.xlsx`);
    toast.success(`Report exported to ${filename}.xlsx successfully!`);

  } catch (error) {
    console.error('Excel export error:', error);
    toast.error('Failed to export Excel file. Please ensure xlsx package is installed.');
  }
};

// Export report to PDF
export const exportToPDF = (data, filename, selectedFields, availableFields, metadata = {}) => {
  if (!data || data.length === 0) {
    toast.error('No data to export');
    return;
  }

  try {
    // Create new PDF document
    const doc = new jsPDF('landscape', 'mm', 'a4');

    // Add title
    const title = `HR Report - ${filename}`;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, 20);

    // Add generation date
    const currentDate = new Date().toLocaleDateString();
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated on: ${currentDate}`, 14, 30);

    // Add record count
    doc.text(`Total Records: ${data.length}`, 14, 35);

    // Add data source info if available
    if (metadata.dataSource) {
      doc.text(`Data Source: ${getDataSourceDisplayName(metadata.dataSource)}`, 14, 40);
    }

    let yPosition = 50;

    // Generate charts data
    const charts = generateChartData(data, metadata.dataSource, selectedFields, availableFields);

    // Add summary statistics
    if (charts.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Summary Statistics', 14, yPosition);
      yPosition += 10;

      // Add chart summaries (text-based representation)
      charts.forEach((chart, index) => {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`${index + 1}. ${chart.title}`, 14, yPosition);
        yPosition += 8;

        // Add chart data in text form
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');

        if (chart.type === 'pie') {
          chart.data.slice(0, 8).forEach(item => {
            const text = `${item.name}: ${item.value} (${item.percentage}%)`;
            doc.text(text, 20, yPosition);
            yPosition += 5;
          });
        } else if (chart.type === 'bar') {
          chart.data.slice(0, 6).forEach(item => {
            const label = item.name || item.range || item.month;
            const value = item.value || item.employees || item.attendance;
            const text = `${label}: ${value}`;
            doc.text(text, 20, yPosition);
            yPosition += 5;
          });
        }

        yPosition += 8; // Add space between charts

        // Check if we need a new page
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
      });

      // Add visual separator
      doc.setDrawColor(220, 38, 38); // Red color
      doc.setLineWidth(0.5);
      doc.line(14, yPosition, 280, yPosition);
      yPosition += 10;
    }

    // Prepare table data
    const tableData = data.slice(0, 500).map(row => { // Limit to 500 rows for PDF performance
      if (selectedFields && selectedFields.length > 0) {
        return selectedFields.map(fieldId => {
          const field = availableFields?.find(f => f.id === fieldId);
          // Remove source prefix from field name to match actual data structure
          const cleanFieldName = fieldId.replace(/^[^_]+_/, '');
          const value = row[cleanFieldName] || '-';
          return formatValueForPDF(value);
        });
      }
      return Object.values(row).map(value => formatValueForPDF(value));
    });

    // Prepare table headers
    const headers = selectedFields && selectedFields.length > 0
      ? selectedFields.map(fieldId => {
          const field = availableFields?.find(f => f.id === fieldId);
          return field?.label || fieldId;
        })
      : Object.keys(data[0] || {});

    // Add table to PDF
    // Use the imported autoTable function
    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: yPosition,
      styles: {
        fontSize: 7,
        cellPadding: 2,
        overflow: 'linebreak',
        halign: 'left',
        valign: 'middle'
      },
      headStyles: {
        fillColor: [220, 38, 38], // Red color matching your theme
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251] // Light gray for alternating rows
      },
      margin: { left: 14, right: 14 },
      tableWidth: 'auto',
      columnWidth: 'auto'
    });

    // Add note if data was truncated
    if (data.length > 500) {
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.text(`Note: Showing first 500 of ${data.length} records. Export to Excel for complete data.`, 14, finalY);
    }
    
    // Add page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
    }
    
    // Save the PDF
    doc.save(`${filename}.pdf`);
    toast.success(`Report exported to ${filename}.pdf successfully!`);
    
  } catch (error) {
    console.error('PDF export error:', error);
    toast.error('Failed to export PDF file. Please try again.');
  }
};

// Helper function to format values for PDF
const formatValueForPDF = (value) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'string' && value.length > 50) {
    return value.substring(0, 47) + '...'; // Truncate long strings
  }
  return String(value);
};

// Generate chart data based on data source and available fields
const generateChartData = (data, dataSource, selectedFields, availableFields) => {
  const charts = [];

  switch (dataSource) {
    case 'employees':
      // Department distribution chart
      if (selectedFields.includes('department_name')) {
        const deptData = {};
        data.forEach(row => {
          const dept = row.department_name || 'Unknown';
          deptData[dept] = (deptData[dept] || 0) + 1;
        });
        const deptChartData = Object.entries(deptData).map(([name, value]) => ({
          name,
          value,
          percentage: ((value / data.length) * 100).toFixed(1)
        }));
        charts.push({
          type: 'pie',
          title: 'Employee Distribution by Department',
          data: deptChartData,
          colors: ['#dc2626', '#059669', '#7c3aed', '#ea580c', '#0891b2', '#be123c']
        });
      }

      // Employment status chart
      if (selectedFields.includes('employment_status')) {
        const statusData = {};
        data.forEach(row => {
          const status = row.employment_status || 'Unknown';
          statusData[status] = (statusData[status] || 0) + 1;
        });
        const statusChartData = Object.entries(statusData).map(([name, value]) => ({
          name,
          value,
          percentage: ((value / data.length) * 100).toFixed(1)
        }));
        charts.push({
          type: 'pie',
          title: 'Employment Status Distribution',
          data: statusChartData,
          colors: ['#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4']
        });
      }

      // Gender distribution chart
      if (selectedFields.includes('gender')) {
        const genderData = {};
        data.forEach(row => {
          const gender = row.gender || 'Unknown';
          genderData[gender] = (genderData[gender] || 0) + 1;
        });
        const genderChartData = Object.entries(genderData).map(([name, value]) => ({
          name,
          value,
          percentage: ((value / data.length) * 100).toFixed(1)
        }));
        charts.push({
          type: 'bar',
          title: 'Gender Distribution',
          data: genderChartData,
          colors: ['#3b82f6', '#ec4899', '#6b7280']
        });
      }
      break;

    case 'attendance':
      // Attendance patterns chart
      if (selectedFields.includes('date') && selectedFields.includes('clock_in')) {
        const monthlyData = {};
        data.forEach(row => {
          if (row.date) {
            const month = new Date(row.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            monthlyData[month] = (monthlyData[month] || 0) + 1;
          }
        });
        const attendanceChartData = Object.entries(monthlyData).map(([month, count]) => ({
          month,
          attendance: count
        }));
        charts.push({
          type: 'line',
          title: 'Monthly Attendance Trends',
          data: attendanceChartData,
          colors: ['#dc2626']
        });
      }
      break;

    case 'leave':
      // Leave types distribution
      if (selectedFields.includes('leave_type')) {
        const leaveTypeData = {};
        data.forEach(row => {
          const leaveType = row.leave_type || 'Unknown';
          leaveTypeData[leaveType] = (leaveTypeData[leaveType] || 0) + 1;
        });
        const leaveChartData = Object.entries(leaveTypeData).map(([name, value]) => ({
          name,
          value,
          percentage: ((value / data.length) * 100).toFixed(1)
        }));
        charts.push({
          type: 'bar',
          title: 'Leave Applications by Type',
          data: leaveChartData,
          colors: ['#059669', '#ea580c', '#7c3aed', '#dc2626', '#0891b2']
        });
      }
      break;

    case 'payroll':
      // Salary range distribution
      if (selectedFields.includes('basic_salary')) {
        const salaryRanges = {
          'Under $3,000': 0,
          '$3,000 - $5,000': 0,
          '$5,000 - $8,000': 0,
          '$8,000 - $12,000': 0,
          'Above $12,000': 0
        };

        data.forEach(row => {
          const salary = parseFloat(row.basic_salary) || 0;
          if (salary < 3000) salaryRanges['Under $3,000']++;
          else if (salary < 5000) salaryRanges['$3,000 - $5,000']++;
          else if (salary < 8000) salaryRanges['$5,000 - $8,000']++;
          else if (salary < 12000) salaryRanges['$8,000 - $12,000']++;
          else salaryRanges['Above $12,000']++;
        });

        const salaryChartData = Object.entries(salaryRanges).map(([range, count]) => ({
          range,
          employees: count,
          percentage: ((count / data.length) * 100).toFixed(1)
        }));

        charts.push({
          type: 'bar',
          title: 'Salary Range Distribution',
          data: salaryChartData,
          colors: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6']
        });
      }
      break;

    default:
      // Generic count by first available field
      if (selectedFields.length > 0 && data.length > 0) {
        const firstField = selectedFields[0];
        const fieldData = {};
        data.forEach(row => {
          const value = row[firstField] || 'Unknown';
          fieldData[value] = (fieldData[value] || 0) + 1;
        });

        // Take top 10 values for readability
        const top10Data = Object.entries(fieldData)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 10)
          .map(([name, value]) => ({
            name: name.length > 20 ? name.substring(0, 17) + '...' : name,
            value,
            percentage: ((value / data.length) * 100).toFixed(1)
          }));

        if (top10Data.length > 0) {
          charts.push({
            type: 'bar',
            title: `Distribution by ${firstField.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`,
            data: top10Data,
            colors: ['#dc2626']
          });
        }
      }
  }

  return charts;
};

// Create charts data sheet for Excel
const createChartsDataSheet = (charts) => {
  const data = [];

  charts.forEach((chart, chartIndex) => {
    // Add chart title
    data.push([`${chartIndex + 1}. ${chart.title}`]);
    data.push(['']); // Empty row

    // Add chart data
    if (chart.type === 'pie') {
      data.push(['Category', 'Count', 'Percentage']);
      chart.data.forEach(item => {
        data.push([item.name, item.value, `${item.percentage}%`]);
      });
    } else if (chart.type === 'bar') {
      const label = chart.data[0]?.range || chart.data[0]?.name || 'Category';
      data.push([label, 'Count', 'Percentage']);
      chart.data.forEach(item => {
        const category = item.range || item.name || item.month;
        const value = item.value || item.employees || item.attendance;
        data.push([category, value, `${item.percentage || 'N/A'}%`]);
      });
    } else if (chart.type === 'line') {
      data.push(['Period', 'Value']);
      chart.data.forEach(item => {
        data.push([item.month, item.attendance]);
      });
    }

    // Add spacing between charts
    data.push(['']);
    data.push(['']);
  });

  return data;
};

// Create summary statistics sheet for Excel
const createSummaryStatsSheet = (data, selectedFields, charts, metadata) => {
  const summaryData = [];

  // Basic statistics
  summaryData.push(['SUMMARY STATISTICS']);
  summaryData.push(['']);
  summaryData.push(['Total Records', data.length, '']);
  summaryData.push(['Data Source', getDataSourceDisplayName(metadata.dataSource || 'Unknown'), '']);
  summaryData.push(['Fields Selected', selectedFields?.length || 0, '']);
  summaryData.push(['Report Generated', new Date().toLocaleString(), '']);
  summaryData.push(['']);

  // Field-specific statistics
  if (selectedFields && selectedFields.length > 0) {
    summaryData.push(['FIELD ANALYSIS']);
    summaryData.push(['']);

    selectedFields.forEach(field => {
      const fieldData = data.map(row => row[field]).filter(val => val !== null && val !== undefined && val !== '');
      const uniqueValues = new Set(fieldData).size;
      const nullValues = data.length - fieldData.length;

      summaryData.push([field, 'Unique Values', uniqueValues]);
      summaryData.push([field, 'Null/Empty Values', nullValues]);
      summaryData.push([field, 'Completion Rate', `${((fieldData.length / data.length) * 100).toFixed(1)}%`]);
      summaryData.push(['']);
    });
  }

  // Chart summaries
  if (charts.length > 0) {
    summaryData.push(['CHART SUMMARIES']);
    summaryData.push(['']);

    charts.forEach((chart, index) => {
      summaryData.push([`Chart ${index + 1}: ${chart.title}`, '', '']);
      const topItem = chart.data[0];
      if (topItem) {
        const value = topItem.value || topItem.employees || topItem.attendance;
        summaryData.push(['Top Category', topItem.name || topItem.range || topItem.month, value]);
      }
      summaryData.push(['Total Categories', chart.data.length, '']);
      summaryData.push(['']);
    });
  }

  return summaryData;
};

// Helper function to create report info sheet for Excel
const createReportInfoSheet = (metadata, recordCount) => {
  const now = new Date();
  const info = [];

  // Report Header
  info.push(['HR PORTAL REPORT']);
  info.push(['']);
  info.push(['']);

  // Basic Information
  info.push(['Report Generated On:', now.toLocaleString()]);
  info.push(['Report Type:', getDataSourceDisplayName(metadata.dataSource || 'Unknown')]);
  info.push(['Total Records:', recordCount.toString()]);
  info.push(['']);

  // Date Range Information
  if (metadata.dateRange) {
    info.push(['Date Range Applied:', formatDateRange(metadata.dateRange, metadata.startDate, metadata.endDate)]);
  }

  // Filter Information
  const appliedFilters = [];
  if (metadata.department && metadata.department !== '') {
    appliedFilters.push(`Department: ${metadata.department}`);
  }
  if (metadata.status && metadata.status !== '') {
    appliedFilters.push(`Status: ${metadata.status}`);
  }

  if (appliedFilters.length > 0) {
    info.push(['Applied Filters:', appliedFilters.join(', ')]);
  } else {
    info.push(['Applied Filters:', 'None']);
  }

  info.push(['']);

  // Field Information
  if (metadata.selectedFields && metadata.selectedFields.length > 0) {
    info.push(['Selected Fields:', metadata.selectedFields.length.toString()]);
    info.push(['']);
    info.push(['Field List:']);

    // Group fields in pairs for better readability
    for (let i = 0; i < metadata.selectedFields.length; i += 2) {
      const field1 = metadata.selectedFields[i];
      const field2 = metadata.selectedFields[i + 1] || '';
      info.push([field1, field2]);
    }
  }

  info.push(['']);
  info.push(['']);
  info.push(['Generated by HR Portal System']);
  info.push(['This report contains sensitive employee data.']);
  info.push(['Please handle with appropriate confidentiality.']);

  return info;
};

// Helper function to get display name for data source
const getDataSourceDisplayName = (dataSource) => {
  const displayNames = {
    employees: 'Employee Data',
    attendance: 'Attendance Records',
    leave: 'Leave Applications',
    payroll: 'Payroll Data',
    training: 'Training & Certifications'
  };
  return displayNames[dataSource] || dataSource || 'Unknown Data Source';
};

// Helper function to format date range for display
const formatDateRange = (dateRange, startDate, endDate) => {
  if (dateRange === 'custom' && startDate && endDate) {
    return `${new Date(startDate).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()}`;
  }

  const rangeLabels = {
    this_month: 'This Month',
    last_month: 'Last Month',
    this_quarter: 'This Quarter',
    last_quarter: 'Last Quarter',
    this_year: 'This Year',
    last_year: 'Last Year'
  };

  return rangeLabels[dateRange] || dateRange || 'No date range specified';
};

// Get available fields for a data source
// Try to fetch server-provided field metadata; fallback to static map
export const getAvailableFieldsAsync = async (dataSource) => {
  if (!dataSource) return [];
  const cacheKey = `reportFieldsMeta_${dataSource}`;

  // Force refresh cache for employees data source to pick up department_name field
  if (dataSource === 'employees') {
    localStorage.removeItem(cacheKey);
    // Also clear the global report fields cache
    localStorage.removeItem('reportFields_GLOBAL');
  }

  try {
    const response = await api.get('/reporting/fields', { params: { source: dataSource } });
    const payload = response?.data;
    const fields = Array.isArray(payload?.fields) ? payload.fields : Array.isArray(payload) ? payload : [];
    const normalized = fields.map((f) => ({
      id: f.id || f.key || f.name,
      label: f.label || f.verbose_name || f.title || (f.id || f.key || f.name),
      type: f.type || f.datatype || 'string',
    })).filter((f) => f.id);
    if (normalized.length > 0) {
      try {
        const schemaVersion = payload?.schema_version || new Date().toISOString();
        localStorage.setItem(cacheKey, JSON.stringify({ schemaVersion, fields: normalized, cachedAt: Date.now() }));
      } catch {}
      return normalized;
    }
  } catch (err) {
    // Log specific error types for better debugging
    if (err.response?.status === 502) {
      console.warn(`⚠️  API server is not responding (502 Bad Gateway) for ${dataSource}. Using cached/static field definitions.`);
    } else if (err.response?.status === 500) {
      console.warn(`⚠️  API server error (500) for ${dataSource}. Using cached/static field definitions.`);
    } else if (err.code === 'ECONNABORTED') {
      console.warn(`⚠️  API request timeout for ${dataSource}. Using cached/static field definitions.`);
    } else {
      console.warn(`Failed to fetch fields for ${dataSource} from API:`, err);
    }

    // Try to use cached data first
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed?.fields) && parsed.fields.length > 0) {
          console.log(`📋 Using cached fields for ${dataSource} (${parsed.fields.length} fields)`);
          return parsed.fields;
        }
      }
    } catch (cacheErr) {
      console.warn(`Failed to parse cached data for ${dataSource}:`, cacheErr);
    }
  }
  return getAvailableFields(dataSource);
};

export const getAvailableFields = (dataSource) => {
  const fieldDefinitions = {
    employees: [
      { id: 'staff_id', label: 'Staff ID', type: 'string', required: true },
      { id: 'first_name', label: 'First Name', type: 'string' },
      { id: 'last_name', label: 'Last Name', type: 'string' },
      { id: 'work_email', label: 'Email', type: 'string' },
      { id: 'alias', label: 'Alias', type: 'string' },
      { id: 'profile_created_at', label: 'Profile Created Date', type: 'datetime' },
      { id: 'has_admin_access', label: 'Admin Access', type: 'boolean' },
      { id: 'has_worker_admin_access', label: 'Worker Admin Access', type: 'boolean' },
      { id: 'work_phone_number', label: 'Work Phone Number', type: 'string' },
      { id: 'did_number', label: 'Direct Line', type: 'string' },
      { id: 'department_name', label: 'Department', type: 'string' },
      { id: 'designation_name', label: 'Designation', type: 'string' },
      { id: 'employment_status', label: 'Status', type: 'string' },
      { id: 'employment_start_date', label: 'Employment Start Date', type: 'date' },
      { id: 'employment_end_date', label: 'Employment End Date', type: 'date' },
      { id: 'probation_confirmation_date', label: 'Probation End Date', type: 'date' },
      { id: 'notice_start_date', label: 'Notice Start Date', type: 'date' },
      { id: 'security_bond_expiry_date', label: 'Security Bond Expiry Date', type: 'date' },
      { id: 'nationality', label: 'Nationality', type: 'string' },
      { id: 'gender', label: 'Gender', type: 'string' },
      { id: 'date_of_birth', label: 'Date of Birth', type: 'date' },
      { id: 'marital_status', label: 'Marital Status', type: 'string' },
      { id: 'classification_name', label: 'Classification', type: 'string' },
      { id: 'employment_classification', label: 'Employment Classification', type: 'string' },
      { id: 'employment_pass_type', label: 'Employment Pass Type', type: 'string' },
      { id: 'extension_number', label: 'Extension Number', type: 'string' },
      { id: 'mom_designation', label: 'MOM Designation', type: 'string' },
      { id: 'trade_category', label: 'Trade Category', type: 'string' },
      { id: 'reporting_officer', label: 'Reporting Officer', type: 'string' },
      { id: 'probation_period_months', label: 'Probation Period (Months)', type: 'number' },
      { id: 'probation_extended', label: 'Probation Extended', type: 'boolean' },
      { id: 'probation_extension_reason', label: 'Probation Extension Reason', type: 'string' },
      { id: 'notice_period_months', label: 'Notice Period (Months)', type: 'number' },
      { id: 'data_retention_period_days', label: 'Data Retention Period (Days)', type: 'number' },
      { id: 'direct_r1_status', label: 'Direct R1 Status', type: 'string' },
      { id: 'mbf_status', label: 'MBF Status', type: 'string' },
      { id: 'work_schedule_template', label: 'Work Schedule Template', type: 'string' },
      { id: 'last_login', label: 'Last Login', type: 'datetime' },
      { id: 'is_superuser', label: 'Superuser', type: 'boolean' },
      { id: 'deleted_at', label: 'Deleted Date', type: 'datetime' }
    ],
    attendance: [
      { id: 'staff_id', label: 'Staff ID', type: 'string' },
      { id: 'employee_name', label: 'Employee Name', type: 'string' },
      { id: 'date', label: 'Date', type: 'date' },
      { id: 'clock_in_time', label: 'Clock In', type: 'datetime' },
      { id: 'clock_out_time', label: 'Clock Out', type: 'datetime' },
      { id: 'hours_worked', label: 'Hours Worked', type: 'number' },
      { id: 'is_late', label: 'Late Arrival', type: 'boolean' },
      { id: 'work_site_name', label: 'Work Site', type: 'string' },
      { id: 'project_name', label: 'Project', type: 'string' },
      { id: 'department_name', label: 'Department', type: 'string' }
    ],
    leave: [
      { id: 'staff_id', label: 'Staff ID', type: 'string' },
      { id: 'employee_name', label: 'Employee Name', type: 'string' },
      { id: 'leave_type_name', label: 'Leave Type', type: 'string' },
      { id: 'leave_start_date', label: 'Start Date', type: 'date' },
      { id: 'leave_end_date', label: 'End Date', type: 'date' },
      { id: 'leave_days', label: 'Days Requested', type: 'number' },
      { id: 'leave_status', label: 'Status', type: 'string' },
      { id: 'applied_date', label: 'Applied Date', type: 'datetime' },
      { id: 'approved_date', label: 'Approved Date', type: 'datetime' },
      { id: 'department_name', label: 'Department', type: 'string' }
    ],
    payroll: [
      { id: 'staff_id', label: 'Staff ID', type: 'string' },
      { id: 'employee_name', label: 'Employee Name', type: 'string' },
      { id: 'basic_salary', label: 'Basic Salary', type: 'number' },
      { id: 'total_allowances', label: 'Total Allowances', type: 'number' },
      { id: 'overtime_amount', label: 'Overtime', type: 'number' },
      { id: 'gross_pay', label: 'Gross Pay', type: 'number' },
      { id: 'cpf_employee', label: 'CPF Employee', type: 'number' },
      { id: 'cpf_employer', label: 'CPF Employer', type: 'number' },
      { id: 'total_deductions', label: 'Total Deductions', type: 'number' },
      { id: 'net_pay', label: 'Net Pay', type: 'number' },
      { id: 'payroll_period', label: 'Payroll Period', type: 'string' }
    ],
    training: [
      { id: 'staff_id', label: 'Staff ID', type: 'string' },
      { id: 'employee_name', label: 'Employee Name', type: 'string' },
      { id: 'certificate_name', label: 'Certificate Name', type: 'string' },
      { id: 'issuing_authority', label: 'Issuing Authority', type: 'string' },
      { id: 'issue_date', label: 'Issue Date', type: 'date' },
      { id: 'expiry_date', label: 'Expiry Date', type: 'date' },
      { id: 'certificate_status', label: 'Status', type: 'string' },
      { id: 'certificate_category', label: 'Category', type: 'string' }
    ]
  };

  return fieldDefinitions[dataSource] || [];
};

// Validate report configuration
export const validateReportConfig = (config) => {
  const errors = [];

  if (!config.dataSource) {
    errors.push('Please select a data source');
  }

  if (!config.selectedFields || config.selectedFields.length === 0) {
    errors.push('Please select at least one field to include in the report');
  }

  // Validate filters
  if (config.filters && config.filters.length > 0) {
    config.filters.forEach((filter, index) => {
      if (filter.field && !filter.value) {
        errors.push(`Filter ${index + 1}: Please provide a value for ${filter.field}`);
      }
    });
  }

  return errors;
};

// Save report configuration
export const saveReportConfig = (config, name) => {
  try {
    const savedReports = JSON.parse(localStorage.getItem('savedReports') || '[]');
    const reportConfig = {
      id: Date.now().toString(),
      name,
      config,
      createdAt: new Date().toISOString()
    };

    savedReports.push(reportConfig);
    localStorage.setItem('savedReports', JSON.stringify(savedReports));

    toast.success(`Report "${name}" saved successfully!`);
    return reportConfig;

  } catch (error) {
    console.error('Save report error:', error);
    toast.error('Failed to save report configuration');
    throw error;
  }
};

// Load saved report configurations
export const loadSavedReports = () => {
  try {
    return JSON.parse(localStorage.getItem('savedReports') || '[]');
  } catch (error) {
    console.error('Load saved reports error:', error);
    return [];
  }
};

// Delete saved report
export const deleteSavedReport = (reportId) => {
  try {
    const savedReports = JSON.parse(localStorage.getItem('savedReports') || '[]');
    const filteredReports = savedReports.filter(report => report.id !== reportId);
    localStorage.setItem('savedReports', JSON.stringify(filteredReports));

    toast.success('Report deleted successfully!');
    return true;

  } catch (error) {
    console.error('Delete report error:', error);
    toast.error('Failed to delete report');
    throw error;
  }
};

// Normalize data returned from reporting/data endpoint
const normalizeReportingData = (payload) => {
  console.log('🔧 normalizeReportingData - Input payload:', payload);
  console.log('🔧 normalizeReportingData - Payload type:', typeof payload);
  console.log('🔧 normalizeReportingData - Is array?', Array.isArray(payload));

  if (!payload) {
    console.log('🔧 normalizeReportingData - No payload, returning empty array');
    return [];
  }

  if (Array.isArray(payload)) {
    console.log('🔧 normalizeReportingData - Payload is array, returning as-is');
    return payload;
  }

  if (payload?.results && Array.isArray(payload.results)) {
    console.log('🔧 normalizeReportingData - Found results array, returning it');
    return payload.results;
  }

  if (Array.isArray(payload?.rows) && Array.isArray(payload?.columns)) {
    console.log('🔧 normalizeReportingData - Found rows/columns structure, processing...');
    const ids = payload.columns.map((c) => c.id || c.key || c.name);
    console.log('🔧 normalizeReportingData - Column IDs:', ids);
    console.log('🔧 normalizeReportingData - Rows count:', payload.rows.length);

    return payload.rows.map((row) => {
      if (Array.isArray(row)) {
        const obj = {};
        ids.forEach((id, idx) => { obj[id] = row[idx]; });
        return obj;
      }
      return row;
    });
  }

  console.log('🔧 normalizeReportingData - No matching structure found, returning empty array');
  return [];
};

// Best-practice data fetch: prefer server /reporting/data, fallback to legacy
export const generateReportSmart = async (dataSource, selectedFields, filters, sortBy, sortOrder) => {
  try {
    const params = { source: dataSource };
    if (selectedFields?.length) params.fields = selectedFields.join(',');
    if (filters?.length) Object.assign(params, buildFilterParams(filters));
    if (sortBy) params.ordering = sortOrder === 'desc' ? `-${sortBy}` : sortBy;

    console.log('🔧 generateReportSmart - Params being sent:', params);
    console.log('🔧 generateReportSmart - Data Source:', dataSource);
    console.log('🔧 generateReportSmart - Selected Fields:', selectedFields);

    const response = await api.get('/reporting/data', { params });

    console.log('🔧 generateReportSmart - Raw API Response:', response);
    console.log('🔧 generateReportSmart - Response Data:', response?.data);
    console.log('🔧 generateReportSmart - Response Status:', response?.status);

    const data = normalizeReportingData(response?.data);
    console.log('🔧 generateReportSmart - Normalized Data:', data);

    return { data };
  } catch (e) {
    console.log('🔧 generateReportSmart - API call failed, falling back to generateReport:', e);
    return await generateReport(dataSource, selectedFields, filters, sortBy, sortOrder);
  }
};

export const previewReportSmart = async (dataSource, selectedFields, filters) => {
  try {
    const params = { source: dataSource, page_size: 100, preview: true };
    if (selectedFields?.length) params.fields = selectedFields.join(',');
    if (filters?.length) Object.assign(params, buildFilterParams(filters));
    const response = await api.get('/reporting/data', { params });
    const data = normalizeReportingData(response?.data);
    return { data: data.slice(0, 100) };
  } catch (e) {
    return await previewReport(dataSource, selectedFields, filters);
  }
};
